import produce from "immer";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { collabDocQuestionSequenceHelper } from "../../helpers/questionSequenceHelpers";
import { AnswerSetApprovalStatus } from "../../types/collab-docs";
import { CollabDocFormDto } from "../../types/dtos/collab-docs";
import CollabDocFlaggedChangeDto from "../../types/dtos/collab-docs/CollabDocFlaggedChangeDto";
import {
  EnforcedCommentType,
  FormComplexities,
  FormQuestionDto,
  NewCommentDto,
  SavedCommentDto,
} from "../../types/dtos/forms";
import {
  BaseUserDetailsDto,
  UserBasicDetailsDto,
} from "../../types/dtos/generic";
import {
  QuestionAnswer,
  QuestionAnswerType,
  QuestionAnswerValue,
  QuestionTasks,
} from "../../types/forms";
import FormQuestion from "../../types/forms/FormQuestion";
import CollabDocQuestion from "./CollabDocQuestion";
import AdvancedTaskDto from "../../types/dtos/forms/AdvancedTaskDto";
import { CollabDocState } from "../../state/CollabDoc/CollabDocState";
import advancedTasksApi from "../../api/task/advancedTasksApi";
import ResultStatus from "../../types/dtos/generic/ResultStatus";
import Toast from "../common/Toast";
import { ToastConfig } from "../common/ToastConfig";
import { AnswerSetCommentVisibility } from "../../types/collab-docs/AnswerSetCommentVisibility";

interface CollabDocFormProps {
  answerSetUniqueId: string;
  answerSetDateCreated: Date;
  form: CollabDocFormDto;
  /** A hex background colour, without the hash */
  backgroundColour: string;
  approvalStatus: AnswerSetApprovalStatus;
  subjectUser: BaseUserDetailsDto;
  isReadOnly: boolean;
  isLocked: boolean;
  comments: Array<SavedCommentDto>;
  participants: Array<UserBasicDetailsDto>;
  answers: Array<QuestionAnswer>;
  tasks: Array<QuestionTasks>;
  activeQuestionId: string | null;
  showValidationErrors: boolean;
  showChangeDetails: boolean;
  flaggedChanges: CollabDocFlaggedChangeDto[];
  formComplexities: FormComplexities;
  /** Whether or not we're in Dual Prep prep mode */
  isInPrepMode: boolean;
  isPrinting: boolean;
  /** The date/time the form data was loaded (must be UTC) */
  dateLoaded: Date;
  docState: CollabDocState;
  /** When the user is submitting, display any submit-time special validations */
  showSubmissionValidationErrors: boolean;
  setDocState(value: Partial<CollabDocState>): void;
  /** A method to call to update the answer state */
  onValueChange(
    questionId: string,
    newValue: QuestionAnswerValue,
    answerType: QuestionAnswerType
  ): QuestionAnswer | null;
  /** When wanting to save a question's answer via the API */
  onValueSave(
    answer: QuestionAnswer,
    nonStateValue: QuestionAnswer | null,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** When attempting to save again what is in the answer state (e.g. on save failure) */
  onRetryValueSave(
    questionId: string,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** When attempting to save again what is in the state for tasks (e.g. on save failure) */
  onRetryTasksSave(
    questionId: string,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** When attempting to save again what is in the state for enforced comments (e.g. on save failure) */
  onRetryEnforcedCommentSave(
    questionId: string,
    behaviourId: number | null,
    goalId: number | null,
    nonStateValue: SavedCommentDto | null,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** The active question determines the state of the comments sidebar */
  onChangeActiveQuestion(activeQuestionId: string): void;
  /** A method to call to mark the comments as seen */
  onCommentsSeen(questionId: string): void;
  /** A method to call to insert a new comment */
  onCommentAdd(
    newComment: NewCommentDto,
    successCallback: () => void,
    errorCallback: () => void
  ): void;
  /** A method to call to delete a comment */
  onCommentDelete(
    commentId: string,
    successCallback: () => void,
    errorCallback: () => void
  ): void;
  /** A method to call to update the state value (single) enforced comment for this question (if one exists, otherwise creates a new comment) */
  onEnforcedCommentEdit(
    questionId: string,
    commentText: string,
    commentFor: EnforcedCommentType,
    objectId: number,
    clientFormId: number
  ): SavedCommentDto;
  /** A method to call to update the enforced comment in the database */
  onEnforcedCommentSave(
    questionId: string,
    behaviourId: number | null,
    goalId: number | null,
    nonStateValue: SavedCommentDto | null,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** When a use adds/edits/deletes a new task */
  onChangeQuestionTasks(
    questionTasks: QuestionTasks,
    onSuccess: () => void,
    onError: () => void
  ): void;
  /** The method to call when the visibility of a comment is changed. `Undefined` for viewer mode as comments are not editable in this mode. */
  onCommentVisibilityChange?(
    commentId: string | number,
    visibility: AnswerSetCommentVisibility
  ): void;
}

const CollabDocForm = ({
  answerSetUniqueId,
  answerSetDateCreated,
  form,
  backgroundColour,
  approvalStatus,
  isReadOnly,
  isLocked,
  comments,
  tasks,
  participants,
  answers,
  activeQuestionId,
  showValidationErrors,
  dateLoaded,
  subjectUser,
  showChangeDetails,
  flaggedChanges,
  formComplexities,
  isInPrepMode,
  isPrinting,
  docState,
  showSubmissionValidationErrors,
  setDocState,
  onValueChange,
  onValueSave,
  onChangeActiveQuestion,
  onCommentsSeen,
  onCommentAdd,
  onCommentDelete,
  onChangeQuestionTasks,
  onEnforcedCommentEdit,
  onEnforcedCommentSave,
  onRetryValueSave,
  onRetryTasksSave,
  onRetryEnforcedCommentSave,
  onCommentVisibilityChange,
}: CollabDocFormProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const tasksApi = new advancedTasksApi(auth.user?.access_token);
  const [questions, setQuestions] = useState<FormQuestionDto[]>([]);
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden()
  );

  /** Get the questions to display, in the correct sequence,
   * including taking into account conditional questions and whether
   * their parents have answers already or not
   */
  const getQuestionsToDisplayForForm = (): Array<FormQuestionDto> => {
    // Get the current form from docState to ensure we have the latest data
    const currentForm = docState.forms.find((f) => f.formId === form.formId);
    const formQuestions = currentForm?.questions || form.questions;

    // Check there are questions to render
    if (!formQuestions || formQuestions.length === 0 || !formQuestions[0]) {
      return [];
    }

    let output: Array<FormQuestionDto> = [];

    const firstQuestion = new FormQuestion(formQuestions[0]);
    output.push(firstQuestion);

    const firstQuestionAnswer = getAnswerForQuestion(firstQuestion.questionId);
    let nextQuestionId =
      collabDocQuestionSequenceHelper.getNextQuestionToDisplay(
        firstQuestion,
        firstQuestionAnswer,
        formQuestions
      );

    let loopCounter = 0;
    const loopCounterSafeLimit = 1000;
    while (nextQuestionId != null && loopCounter < loopCounterSafeLimit) {
      const dtoMatch = formQuestions.find(
        (x) => x.questionId === nextQuestionId // eslint-disable-line no-loop-func
      );
      if (!dtoMatch) {
        // Unable to match question. This shouldn't happen. Break the loop.
        nextQuestionId = null;
      } else {
        // Add the matching question to the output, and find the next question after that
        const nextQuestion = new FormQuestion(dtoMatch);
        output.push(nextQuestion);

        // Now attempt to calculate the next question
        const loopQuestionAnswer = getAnswerForQuestion(
          nextQuestion.questionId
        );
        nextQuestionId =
          collabDocQuestionSequenceHelper.getNextQuestionToDisplay(
            nextQuestion,
            loopQuestionAnswer,
            formQuestions
          );
      }
    }

    return output;
  };

  const getAnswerForQuestion = (questionId: string): QuestionAnswerValue => {
    const match = answers.find((x) => x.questionId === questionId);
    return match ? match.answer : null;
  };

  useEffect(() => {
    const nextQuestionState = getQuestionsToDisplayForForm();
    setQuestions(nextQuestionState);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const nextQuestionState = getQuestionsToDisplayForForm();
    setQuestions(nextQuestionState);
  }, [answers, docState.forms]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChangeAdvancedTasks = (
    questionId: string,
    tasks?: AdvancedTaskDto[],
    cancelledTasks?: AdvancedTaskDto[],
    newTasks?: AdvancedTaskDto[]
  ) => {
    // Create new form state and update the relevant form/question with new tasks/cancelledTasks
    const nextFormState = docState.forms.map((f) => {
      if (f.formId === form.formId) {
        const newFormState = {
          ...f,
        };
        const questionIndex = newFormState.questions.findIndex(
          (x) => x.questionId === questionId
        );
        if (tasks) {
          newFormState.questions[questionIndex].taskManagementConfig!.tasks =
            tasks;
        }
        if (cancelledTasks) {
          newFormState.questions[
            questionIndex
          ].taskManagementConfig!.cancelledTasks = cancelledTasks;
        }
        if (newTasks) {
          newFormState.questions[questionIndex].taskManagementConfig!.newTasks =
            newTasks;
        }

        // Combine the task and newTasks and update the joint list
        const liveAndNewTasks = [
          ...newFormState.questions[questionIndex].taskManagementConfig!.tasks,
          ...newFormState.questions[questionIndex].taskManagementConfig!
            .newTasks,
        ];
        newFormState.questions[
          questionIndex
        ].taskManagementConfig!.liveAndNewTasks = liveAndNewTasks;

        return newFormState;
      }
      return f;
    });

    setDocState({ forms: nextFormState });
  };

  const onSwapTask = (
    taskId: string,
    fromQuestionId: string,
    toQuestionId: string,
    isSwappingToNew: boolean
  ) => {
    // Find both questions across all forms
    let fromQuestion: FormQuestionDto | null = null;
    let toQuestion: FormQuestionDto | null = null;
    let fromFormId: number | null = null;
    let toFormId: number | null = null;

    for (const f of docState.forms) {
      const fromMatch = f.questions.find(
        (q) => q.questionId === fromQuestionId
      );
      const toMatch = f.questions.find((q) => q.questionId === toQuestionId);

      if (fromMatch && !fromQuestion) {
        fromQuestion = fromMatch;
        fromFormId = f.formId;
      }
      if (toMatch && !toQuestion) {
        toQuestion = toMatch;
        toFormId = f.formId;
      }

      if (fromQuestion && toQuestion) break;
    }

    if (
      !fromQuestion ||
      !toQuestion ||
      !fromQuestion.taskManagementConfig ||
      !toQuestion.taskManagementConfig
    ) {
      console.error(
        "Cannot swap task: questions not found or missing task management config"
      );
      return;
    }

    // Extract primitive values from the destination question's config BEFORE async call
    // This prevents accessing a revoked Immer proxy in the async callback
    const toConfig = toQuestion.taskManagementConfig;
    const enforceComments = toConfig.enforceComments;
    const subjectEmployeeId = toConfig.subjectEmployeeId;
    const questionMode = toConfig.questionMode;

    // Make the API call with the destination question's config
    if (isSwappingToNew) {
      tasksApi.treatTaskAsNewTask(
        taskId,
        answerSetUniqueId,
        enforceComments,
        subjectEmployeeId,
        answerSetDateCreated,
        questionMode,
        (result: ResultStatus) => {
          if (result.success && result.data) {
            // Use the DTO returned from the API
            const updatedTask: AdvancedTaskDto = result.data;

            // Update state with the swapped task
            // Handle questions that might be in different forms
            const nextFormState = docState.forms.map((f) => {
              const fromQuestionIndex = f.questions.findIndex(
                (q) => q.questionId === fromQuestionId
              );
              const toQuestionIndex = f.questions.findIndex(
                (q) => q.questionId === toQuestionId
              );

              // Only update this form if it contains one of the questions
              if (fromQuestionIndex === -1 && toQuestionIndex === -1) {
                return f;
              }

              const newFormState = { ...f };
              const newQuestions = [...newFormState.questions];

              // Update from question if it's in this form
              if (fromQuestionIndex !== -1) {
                const fromQuestion = { ...newQuestions[fromQuestionIndex] };
                if (fromQuestion.taskManagementConfig) {
                  const fromConfig = { ...fromQuestion.taskManagementConfig };
                  fromConfig.tasks = fromConfig.tasks.filter(
                    (t) => t.taskId !== updatedTask.taskId
                  );
                  fromConfig.newTasks = fromConfig.newTasks.filter(
                    (t) => t.taskId !== updatedTask.taskId
                  );
                  fromConfig.liveAndNewTasks = [
                    ...fromConfig.tasks,
                    ...fromConfig.newTasks,
                  ];
                  fromQuestion.taskManagementConfig = fromConfig;
                  newQuestions[fromQuestionIndex] = fromQuestion;
                }
              }

              // Update to question if it's in this form
              if (toQuestionIndex !== -1) {
                const toQuestion = { ...newQuestions[toQuestionIndex] };
                if (toQuestion.taskManagementConfig) {
                  const toConfig = { ...toQuestion.taskManagementConfig };
                  // Add task to destination question (as new task)
                  toConfig.newTasks = [
                    ...toConfig.newTasks.filter(
                      (t) => t.taskId !== updatedTask.taskId
                    ),
                    updatedTask,
                  ];
                  toConfig.liveAndNewTasks = [
                    ...toConfig.tasks,
                    ...toConfig.newTasks,
                  ];
                  toQuestion.taskManagementConfig = toConfig;
                  newQuestions[toQuestionIndex] = toQuestion;
                }
              }

              return {
                ...newFormState,
                questions: newQuestions,
              };
            });

            setDocState({ forms: nextFormState });
            setToastConfig(
              ToastConfig.success(
                "Success",
                "Task has been moved to the setting question"
              )
            );
          } else {
            console.error("Failed to swap task:", result.message);
            setToastConfig(
              ToastConfig.error(
                "Error",
                result.message || "Failed to move task. Please try again."
              )
            );
          }
        },
        (error: any) => {
          console.error("Error swapping task to setting question:", error);
          setToastConfig(
            ToastConfig.error(
              "Error",
              "An error occurred while moving the task. Please try again."
            )
          );
        }
      );
    } else {
      tasksApi.treatTaskAsExistingTask(
        taskId,
        answerSetUniqueId,
        enforceComments,
        subjectEmployeeId,
        answerSetDateCreated,
        questionMode,
        (result: ResultStatus) => {
          if (result.success && result.data) {
            // Use the DTO returned from the API
            const updatedTask: AdvancedTaskDto = result.data;

            // Update state with the swapped task
            // Handle questions that might be in different forms
            const nextFormState = docState.forms.map((f) => {
              const fromQuestionIndex = f.questions.findIndex(
                (q) => q.questionId === fromQuestionId
              );
              const toQuestionIndex = f.questions.findIndex(
                (q) => q.questionId === toQuestionId
              );

              // Only update this form if it contains one of the questions
              if (fromQuestionIndex === -1 && toQuestionIndex === -1) {
                return f;
              }

              const newFormState = { ...f };
              const newQuestions = [...newFormState.questions];

              // Update from question if it's in this form
              if (fromQuestionIndex !== -1) {
                const fromQuestion = { ...newQuestions[fromQuestionIndex] };
                if (fromQuestion.taskManagementConfig) {
                  const fromConfig = { ...fromQuestion.taskManagementConfig };
                  fromConfig.tasks = fromConfig.tasks.filter(
                    (t) => t.taskId !== updatedTask.taskId
                  );
                  fromConfig.newTasks = fromConfig.newTasks.filter(
                    (t) => t.taskId !== updatedTask.taskId
                  );
                  fromConfig.liveAndNewTasks = [
                    ...fromConfig.tasks,
                    ...fromConfig.newTasks,
                  ];
                  fromQuestion.taskManagementConfig = fromConfig;
                  newQuestions[fromQuestionIndex] = fromQuestion;
                }
              }

              // Update to question if it's in this form
              if (toQuestionIndex !== -1) {
                const toQuestion = { ...newQuestions[toQuestionIndex] };
                if (toQuestion.taskManagementConfig) {
                  const toConfig = { ...toQuestion.taskManagementConfig };
                  // Add task to destination question (as existing task)
                  toConfig.tasks = [
                    ...toConfig.tasks.filter(
                      (t) => t.taskId !== updatedTask.taskId
                    ),
                    updatedTask,
                  ];
                  toConfig.liveAndNewTasks = [
                    ...toConfig.tasks,
                    ...toConfig.newTasks,
                  ];
                  toQuestion.taskManagementConfig = toConfig;
                  newQuestions[toQuestionIndex] = toQuestion;
                }
              }

              return {
                ...newFormState,
                questions: newQuestions,
              };
            });

            setDocState({ forms: nextFormState });
            setToastConfig(
              ToastConfig.success(
                "Success",
                "Task has been moved to the review question"
              )
            );
          } else {
            console.error("Failed to swap task:", result.message);
            setToastConfig(
              ToastConfig.error(
                "Error",
                result.message || "Failed to move task. Please try again."
              )
            );
          }
        },
        (error: any) => {
          console.error("Error swapping task to review question:", error);
          setToastConfig(
            ToastConfig.error(
              "Error",
              "An error occurred while moving the task. Please try again."
            )
          );
        }
      );
    }
  };

  return (
    <div className="px-2 lg:px-4">
      <div className="shadow-xl rounded-lg mb-6 bg-white">
        <h3 className=" px-4 py-3 text-left text-gray-600 bg-gray-50 border-b border-gray-200 rounded-t-lg font-semibold">
          {t(form.title)}
        </h3>
        <div className="pb-2">
          {questions.map((q) => {
            // Get the comments for this question
            const questionComments = comments.filter(
              (x) => x.questionId === q.questionId
            );
            // Get the flagged changes for this question
            const questionFlaggedChanges = flaggedChanges.filter(
              (x) => x.questionId === q.questionId
            );

            // Get the latest question data from docState to ensure we have updated task config
            // Search across all forms since questions might be in different forms
            let latestQuestionDto = q;
            for (const f of docState.forms) {
              const found = f.questions.find(
                (x) => x.questionId === q.questionId
              );
              if (found) {
                latestQuestionDto = found;
                break;
              }
            }
            const formQuestion = new FormQuestion(latestQuestionDto);

            // Keep page-breaks in JSON / sequenceMap but do not mount a control in collab.
            if (formQuestion.questionType === "JOURNEY-PAGE-BREAK") {
              return null;
            }

            const currentAnswer =
              answers.find((x) => x.questionId === formQuestion.questionId) ||
              null;

            const questionTasks = tasks.find(
              (x) => x.questionId === formQuestion.questionId
            );
            return (
              <CollabDocQuestion
                answerSetUniqueId={answerSetUniqueId}
                answerSetDateCreated={answerSetDateCreated}
                key={`question_${q.questionId}`}
                question={formQuestion}
                currentAnswer={currentAnswer}
                onValueChange={onValueChange}
                onValueSave={onValueSave}
                isReadOnly={isReadOnly}
                isDocLocked={isLocked}
                formActiveQuestionId={activeQuestionId}
                comments={questionComments}
                flaggedChanges={questionFlaggedChanges}
                showPlanningResponses={form.showPlanningResponses}
                participants={participants}
                formApprovalStatus={approvalStatus}
                showValidationErrors={showValidationErrors}
                dateFormLoaded={dateLoaded}
                subjectUser={subjectUser}
                tasks={questionTasks ? questionTasks : null}
                formColor={backgroundColour}
                showChangeDetails={showChangeDetails}
                formComplexities={formComplexities}
                isInPrepMode={isInPrepMode}
                isPrinting={isPrinting}
                isClientJourneyClosed={docState.isClientJourneyClosed}
                approvalFlow={docState.approvalFlow}
                onQuestionFocus={onChangeActiveQuestion}
                onCommentAdd={onCommentAdd}
                onCommentDelete={onCommentDelete}
                onCommentsSeen={onCommentsSeen}
                onChangeQuestionTasks={onChangeQuestionTasks}
                onEnforcedCommentEdit={onEnforcedCommentEdit}
                onEnforcedCommentSave={onEnforcedCommentSave}
                onRetryValueSave={onRetryValueSave}
                onRetryTasksSave={onRetryTasksSave}
                onRetryEnforcedCommentSave={onRetryEnforcedCommentSave}
                onChangeAdvancedTasks={onChangeAdvancedTasks}
                onSwapTask={onSwapTask}
                onCommentVisibilityChange={onCommentVisibilityChange}
                showSubmissionValidationErrors={showSubmissionValidationErrors}
                advancedTaskTypeSwapabilities={
                  docState.advancedTaskTypeSwapabilities
                }
              />
            );
          })}
        </div>
      </div>
      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </div>
  );
};

export default CollabDocForm;
